import * as Sentry from '@sentry/browser'
import { initHelper } from './analytics'
import { store } from './store/index'
import { get } from './util'

const blackListedEventCodes = ['incomplete_number']

export function checkoutBrowserSentry(keyName: string, environment: string) {
  const keys = document.getElementById('fedora-keys')
  if (!keys) return
  const dsn = keys.dataset[keyName]
  if (!dsn || dsn === 'null') return
  Sentry.init({
    release: keys.dataset['commitSha'],
    environment,
    dsn,
    beforeBreadcrumb: filterOutAnalytics,
    beforeSend: filterOutBlackListed,
  })
  Sentry.configureScope(scope => {
    scope.setTag('feature', 'checkout')
    scope.setTag('school id', initHelper.getSchoolId())
    scope.addEventProcessor(event => {
      event.extra = Object.assign(snapshotState(), event.extra || {})
      return event
    })
  })
}

function filterOutAnalytics(breadcrumb: Sentry.Breadcrumb, hint) {
  if (breadcrumb.type === 'http') {
    const url = get(breadcrumb, 'data.url') || ''
    const urlContainsEventable = url.indexOf('eventable') > 0
    const urlContainsAhoy = url.indexOf('ahoy/events') > 0
    return urlContainsEventable || urlContainsAhoy ? null : breadcrumb
  }
  return breadcrumb
}

/* Sentry has an option for filtering, but it's only regex, this can be more precise */
function filterOutBlackListed(event, hint?) {
  try {
    const messageObject = JSON.parse(event.message)
    if (blackListedEventCodes.includes(messageObject.code)) {
      return null
    }
  } catch (e) {
    if (e instanceof SyntaxError) {
      return event
    }
    throw e
  }
  return event
}

export const Logger = {
  fatal(err: string | Error, metadata: Record<string, any> = {}) {
    Sentry.withScope(scope => {
      scope.setExtras(metadata)
      if (typeof err === 'string') {
        Sentry.captureMessage(err, Sentry.Severity.Fatal)
      } else {
        Sentry.captureException(err)
      }
    })
  },
  error(err: string | Error, metadata: Record<string, any> = {}) {
    Sentry.withScope(scope => {
      scope.setExtras(metadata)
      if (typeof err === 'string') {
        Sentry.captureMessage(err, Sentry.Severity.Error)
      } else {
        Sentry.captureException(err)
      }
    })
  },
  info(msg: string, metadata: Record<string, any> = {}) {
    Sentry.withScope(scope => {
      scope.setExtras(metadata)
      Sentry.captureMessage(msg, Sentry.Severity.Info)
    })
  },
  setTags(tags: Record<string, string | number | undefined>) {
    Sentry.configureScope(scope => {
      Object.entries(tags).forEach(([k, v]) => scope.setTag(k, String(v)))
    })
  },
  addBreadcrumb(message, breadcrumb: Omit<Sentry.Breadcrumb, 'message'> = {}) {
    Sentry.addBreadcrumb({
      level: Sentry.Severity.Info,
      ...breadcrumb,
      message,
      category: breadcrumb.category
        ? `log:${breadcrumb.category}`
        : 'log:generic',
    })
  },
  catchHandler(err: Error) {
    Sentry.captureException(err)
  },
}

function snapshotState() {
  const state = store.getState()
  if (state) {
    return {
      currentPaymentMethod: state.ui.currentPaymentMethod,
      shouldPreventSubmit: state.ui.shouldPreventSubmit,
      hasPaymentBeenSubmitted: state.ui.hasPaymentBeenSubmitted,
      isRequestInProgress: state.ui.isRequestInProgress,
      stripe_temporary_token: Boolean(state.user.stripe_temporary_token),
      'stripe.isReady': state.stripe.isReady,
    }
  }
  return {}
}
